import React, { useState, useEffect, useRef } from "react";
import * as Styled from "./styles";
import { ASSETS } from "../../utils/config";
import NotFound from "../../assets/images/not-found.svg";
import UserItem from "./UserItem";
import { TextView } from "@components";
import WheelItem from "./WheelItem";
import { searchUsers, searchWheels } from "../../services/api";
import BrandWheelLoader from "../BrandWheelLoader";
import Tabs from "../Tabs";
import { AnimatePresence, useReducedMotion } from "framer-motion";
import { useSelector } from "react-redux";
import {
  SEARCH_KEYBOARD_NAV_ENABLED,
  SEARCH_INPUT_HARDENING_ENABLED,
  SEARCH_UNIFIED_FIELD_BORDER_ENABLED,
} from "../../constants/featureFlags";
import {
  searchNavAction,
  searchResultsOpen,
  isActivationKey,
} from "./searchKeyboardNav";
import { searchAnnouncement } from "./searchAnnouncement";
import {
  SEARCH_MAX_QUERY_LEN,
  clampSearchQuery,
  shouldShowSearchError,
  shouldShowEmptyState,
  getSearchErrorMessage,
} from "./searchInputGuard";
import { searchIconVariants, searchClearIconVariants } from "./searchMotion";
import { createSearchRequestSeq } from "./searchRequestSeq";
import { colors } from "../../utils/theme";
import useScreenSize from "../../hooks/useScreenSize";

// #965: global search only covers Users and Wheels — Collections are
// intentionally not searchable here.
const BASE_TABS = ["Users", "Wheels"];

// EW-1182 keyboard-nav ARIA ids (only referenced when the flag is ON).
const SEARCH_LISTBOX_ID = "iyuga-search-results-listbox";
const searchOptionId = (tab, index) => `iyuga-search-opt-${tab}-${index}`;

export default function SearchBar({
  setModalOpen = null,
  setOnLeave = null,
  handleNavigate = null,
}) {
  const [searchText, setSearchText] = useState("");
  const [wheels, setWheels] = useState([]);
  const [users, setUsers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [loading, setLoading] = useState(false);
  const searchContainerRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedTab, setSelectedTab] = useState(BASE_TABS[0]);
  // kelly-0923-P1-search-default-tab-relevance-and-wheel-count-mismatch
  // (finding 1): when the Users-empty-Wheels-has-matches fallback below
  // (see fetchData's isFreshQuery branch) switches selectedTab itself, the
  // [selectedTab] effect a few lines down would otherwise re-fetch Wheels a
  // SECOND time for the same query. Set right before that programmatic
  // setSelectedTab call so the effect's very next run is a no-op once.
  const skipNextTabEffectRef = useRef(false);

  const [pageUsers, setPageUsers] = useState(1);
  const [pageWheels, setPageWheels] = useState(1);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [hasMoreWheels, setHasMoreWheels] = useState(true);
  const itemsContainerRef = useRef(null);

  // EW-1182: index of the keyboard-highlighted result (-1 = none). Only read
  // when SEARCH_KEYBOARD_NAV_ENABLED; setting it to -1 while OFF is an
  // idempotent no-op (React bails on an unchanged primitive) so OFF stays
  // byte-identical.
  const [highlightIndex, setHighlightIndex] = useState(-1);

  // EW-1182 (Esc-to-close re-fix): an explicit Escape dismisses the results
  // popup even while the typed query remains in the input. Reset to false the
  // moment the user types again (or clears), so re-typing reopens. Only ever set
  // true by the flag-gated Escape handler, so flag-OFF keeps it false forever.
  const [dismissed, setDismissed] = useState(false);

  // EW-1181: true when the last search request failed (network/CDN/other). Only
  // ever set by the flag-gated catch branch, so flag-OFF it stays false forever
  // and the dropdown renders byte-identical to today.
  const [fetchError, setFetchError] = useState(false);
  // ISP-9999: HTTP status of that failure (e.g. 429 from the shared
  // search-users/search-wheels rate limit), so the banner text can tell a
  // "you're going too fast" throttle apart from a real network/CDN failure
  // instead of showing the same "check your connection" message for both.
  const [fetchErrorStatus, setFetchErrorStatus] = useState(null);

  // GS-H11: monotonic request tokens so an out-of-order (superseded) search
  // response can never overwrite the results of the query the user is actually
  // looking at. Lazily created once; the ref keeps it stable across renders.
  const requestSeqRef = useRef(null);
  if (requestSeqRef.current === null) {
    requestSeqRef.current = createSearchRequestSeq();
  }

  // GS-2.14: true for the lifetime of an uncommitted IME composition (real
  // `compositionstart`→`compositionend` lifecycle, see handleCompositionStart/
  // End below). `composingRef` is read synchronously inside handleInputChange
  // (a ref, not state, so there is no risk of reading a stale value from an
  // in-flight React batch); `composing` (state) drives the busy indicator so
  // typing mid-composition shows "Searching…"/the loader instead of a false
  // empty state, without ever touching the real `loading` flag (qa-validator
  // rounds 1+2 both found that faking `loading` — via an early return that
  // skipped clearing it, then via a timer that raced a real fetch — created a
  // new stuck/false-empty-state defect each time. Composing is now tracked
  // entirely separately from `loading` on purpose.)
  const composingRef = useRef(false);
  const [composing, setComposing] = useState(false);

  const wheelInformation = useSelector((state) => state.segmentBuilder);
  const prevIndexRef = useRef(wheelInformation?.currentIndex);

  // Respect the OS "reduce motion" setting: collapse the icon scale/rotate
  // keyframes to a plain fade so the search affordances don't animate for
  // motion-sensitive users (motion-language law).
  const reduceMotion = useReducedMotion();

  // search-comp-vY-01: same mobilePortrait cut the results overlay's own CSS
  // already keys off (device.mobilePortrait). Read once here so it can gate
  // Container's fixed-flex-column layout below.
  const { isMobilePortrait } = useScreenSize();

  const fetchData = async (text, type, page, { isFreshQuery = false } = {}) => {
    // GS-H11: claim this request's token BEFORE anything else runs. Issuing it
    // up here also means the empty-query early return below supersedes whatever
    // is still in flight, so a late response cannot re-open a cleared dropdown.
    const token = SEARCH_INPUT_HARDENING_ENABLED
      ? requestSeqRef.current.issue()
      : 0;
    /** True once a newer query has been issued -> this response is stale. */
    const superseded = () =>
      SEARCH_INPUT_HARDENING_ENABLED && !requestSeqRef.current.isCurrent(token);

    if (!text.trim()) {
      setUsers([]);
      setWheels([]);
      setShowResults(false);
      // An empty query fires NO request, so nothing downstream will ever flip
      // `loading` back off. Without this the spinner outlives the query that
      // started it and (via searchResultsOpen) pins the dropdown open forever.
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // EW-1181: a fresh (page 1) attempt clears any prior failure banner.
      if (SEARCH_INPUT_HARDENING_ENABLED && page === 1) {
        setFetchError(false);
        setFetchErrorStatus(null);
      }
      let response;

      if (type === "Users") {
        response = await searchUsers(text, page, 10);
        // GS-H11: a newer query was issued while this was in flight. Drop the
        // whole response — committing it would show results for a query the
        // user has already replaced.
        if (superseded()) return;
        const { results } = response.data;

        if (page === 1) {
          setUsers(results);
        } else {
          setUsers((prevUsers) => [...prevUsers, ...results]);
        }

        setHasMoreUsers(results.length === 10);

        if (page === 1) {
          const hasResults = results.length > 0;
          setShowResults(hasResults);

          // kelly-0923-P1-search-default-tab-relevance-and-wheel-count-mismatch
          // (finding 1): MEASURED live on web-dev (2026-09-24) -- searching
          // "Toyota Motors" (0 users, 2 exact wheel-title matches) opened on
          // the Users tab reading "No Users Found" while Wheels had the
          // matches the whole time. A fresh query only ever fetched the
          // currently-selected tab, never checking whether the OTHER tab
          // actually had something to show. On a brand-new query (not a
          // manual tab click, not pagination) with zero Users matches, probe
          // Wheels too and switch to it if it has results -- opening on the
          // first tab (Users, then Wheels) that isn't empty.
          if (isFreshQuery && !hasResults) {
            try {
              const wheelsResponse = await searchWheels(text, 1, 10);
              if (superseded()) return;
              const wheelsResults = wheelsResponse.data.results;
              setWheels(wheelsResults);
              setHasMoreWheels(wheelsResults.length === 10);
              setShowResults(hasResults || wheelsResults.length > 0);
              if (wheelsResults.length > 0) {
                skipNextTabEffectRef.current = true;
                setSelectedTab("Wheels");
              }
            } catch (wheelsError) {
              // The fallback probe itself failing must not surface a second/
              // confusing error banner for a request the user never directly
              // asked for -- stay on the Users tab's (correct) empty state.
              console.error(
                "Error probing Wheels for the default-tab fallback:",
                wheelsError
              );
            }
          }
        }
      } else if (type === "Wheels") {
        response = await searchWheels(text, page, 10);
        // GS-H11: same stale-response guard as the Users branch.
        if (superseded()) return;
        const { results } = response.data;

        if (page === 1) {
          setWheels(results);
        } else {
          setWheels((prevWheels) => [...prevWheels, ...results]);
        }

        setHasMoreWheels(results.length === 10);

        if (page === 1) {
          const hasResults = results.length > 0;
          setShowResults(hasResults);
        }
      }

      setLoading(false);
    } catch (error) {
      // GS-H11: a stale failure must not clear the CURRENT query's spinner or
      // raise an error banner over results that loaded fine.
      if (superseded()) return;
      setLoading(false);
      console.error(`Error fetching ${type.toLowerCase()} results:`, error);
      // EW-1181: surface the failure instead of silently swallowing it. Clear
      // the stale rows from the previous (successful) query so the dropdown does
      // not lie, and flip on the error banner. Only for a page-1 (fresh) fetch —
      // an infinite-scroll page-N failure keeps the rows already shown.
      if (SEARCH_INPUT_HARDENING_ENABLED && page === 1) {
        if (type === "Users") {
          setUsers([]);
        } else if (type === "Wheels") {
          setWheels([]);
        }
        setFetchError(true);
        // ISP-9999: capture the status so the banner can tell a 429 (shared
        // search-users/search-wheels throttle — this account is very
        // frequently rate-limited by normal use, see searchInputGuard.js)
        // apart from a real network/CDN failure.
        setFetchErrorStatus(error?.response?.status ?? null);
        setShowResults(true);
      }
    }
  };

  const handleInputChange = (event) => {
    // EW-1181: cap the query so an over-long paste can never be sent as a URL
    // that fails at the CDN/LB. The maxLength attribute already caps typed/pasted
    // input at the browser level; this is the belt-and-suspenders clamp for any
    // path that bypasses it. Flag-OFF uses the raw value (byte-identical).
    const value = SEARCH_INPUT_HARDENING_ENABLED
      ? clampSearchQuery(event.target.value)
      : event.target.value;
    setSearchText(value);
    setPageUsers(1);
    setPageWheels(1);
    setHasMoreUsers(true);
    setHasMoreWheels(true);
    setHighlightIndex(-1); // EW-1182: a new query drops any stale highlight.
    setDismissed(false); // EW-1182: typing after an Escape reopens the dropdown.
    if (SEARCH_INPUT_HARDENING_ENABLED) {
      setFetchError(false); // EW-1181: editing the query clears the error banner.
      setFetchErrorStatus(null);
    }

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Emptying the box resolves the dropdown IMMEDIATELY rather than 500ms
    // later. Waiting the full debounce left the popup hanging on the previous
    // query's rows/spinner after the input was already blank.
    if (!value.trim()) {
      // GS-H11: emptying the box fires NO new request, so nothing would other-
      // wise supersede one already in flight — it would land later and re-open
      // the dropdown over an empty input. Invalidate explicitly.
      if (SEARCH_INPUT_HARDENING_ENABLED) {
        requestSeqRef.current.invalidate();
      }
      setTypingTimeout(null);
      setUsers([]);
      setWheels([]);
      setShowResults(false);
      setLoading(false);
      return;
    }

    // GS-2.14 (qa-validator 2026-08-06 ROUND 3 rebuild — rounds 1/2 both found
    // a new defect in a timer-based fallback, because a setTimeout races both
    // the real fetch AND the human): skip scheduling a search while an IME
    // (CJK/Japanese/Korean) composition is still uncommitted, tracked via the
    // REAL composition lifecycle (`onCompositionStart`/`onCompositionEnd`
    // below), not inferred per-keystroke from `event.nativeEvent.isComposing`.
    // `compositionend` is GUARANTEED by spec to fire on commit, on Escape-
    // cancel, AND on the field losing focus mid-composition (a browser must
    // resolve an open composition before blur) -- so `composingRef` can never
    // get permanently stuck the way a timer-based guess could. MEASURED
    // (fleet/w3-search-2 R4 2026-07-26, fe16.json GS_2_14_ime): a
    // compositionstart -> "ni" compositionupdate sequence, WITHOUT committing,
    // fired a premature `search-users?search=ni` request on the un-committed
    // candidate. searchText/dismissed/etc. above still update every keystroke
    // so the box visibly shows the in-progress candidate.
    if (composingRef.current) {
      // Do NOT touch `loading` here and do NOT schedule any timer. Busy-state
      // while composing is driven by the SEPARATE `composing` render flag
      // (set by handleCompositionStart, below) -- not by faking `loading` --
      // so there is no timer that can ever fire while an unrelated real fetch
      // is in flight (the exact regression a timer-based fallback introduced
      // twice: qa-validator round 2 measured it painting "No Users Found"
      // over a still-pending request). handleCompositionEnd re-invokes this
      // function with the committed value once composition actually resolves.
      setTypingTimeout(null);
      return;
    }

    // GS-8.8: mark busy for the whole debounce window, not just once fetchData
    // actually starts. Previously `loading` stayed false until the 500ms timer
    // fired, so `shouldShowEmptyState` (!loading && searchText && listEmpty) was
    // TRUE the instant a key was pressed -> the panel opened showing "No Users
    // Found" against the PREVIOUS query's stale/empty list before any request
    // for the new query had even been sent. Setting loading here makes the
    // debounce window render the existing loader instead (and upgrades the
    // live-region announcement to "Searching…" via searchAnnouncement's
    // loading-takes-precedence rule) — same mechanism already used on tab
    // switch (below), just applied consistently to typing too.
    setLoading(true);

    const newTimeout = setTimeout(() => {
      fetchData(value, selectedTab, 1, { isFreshQuery: true });
    }, 500);

    setTypingTimeout(newTimeout);
  };

  // GS-2.14: the real composition lifecycle. `compositionstart` fires once,
  // before any `input` events for that composition; `compositionend` is
  // spec-guaranteed to fire on commit, on cancel, AND on blur mid-composition
  // — so `composingRef` cannot get stuck true forever the way an inferred/
  // timer-based guess could (qa-validator rounds 1+2).
  const handleCompositionStart = () => {
    composingRef.current = true;
    setComposing(true);
  };

  const handleCompositionEnd = (event) => {
    composingRef.current = false;
    setComposing(false);
    // The DOM value is already the FINAL committed text by the time
    // compositionend fires (spec-guaranteed) — re-run the normal pipeline
    // with it, exactly as if this were any other keystroke.
    handleInputChange(event);
  };

  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [typingTimeout]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setShowResults(false);
        // Clicking away must actually CLOSE the popup. setShowResults(false)
        // alone does not: the render gate is
        // searchResultsOpen({dismissed, showResults, searchText, loading}),
        // whose `showResults || searchText || loading` term keeps the panel
        // mounted for as long as the typed query stays in the box — so the
        // dropdown hung over the page after every outside click.
        // MEASURED web-stg (bundle main.1d538a78.js, 2026-07-28): query
        // "meditation" -> listbox mounted, aria-expanded "true"; dispatch
        // mousedown/mouseup/click at (900,20) outside the field -> STILL
        // mounted, aria-expanded STILL "true". Control: Escape on the same
        // session unmounted it, proving the probe can see a closed panel.
        // `dismissed` is the same override Escape uses, so click-away and
        // Escape now share one dismissal path (and one reopen path: typing,
        // Arrow keys, or clearing all reset it). Flag-OFF never sets it.
        if (SEARCH_KEYBOARD_NAV_ENABLED) {
          setDismissed(true);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchContainerRef]);

  useEffect(() => {
    const handleScroll = () => {
      if (loading) return;

      const container = itemsContainerRef.current;
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        if (scrollTop + clientHeight >= scrollHeight - 5) {
          // Adjust threshold as needed
          if (selectedTab === "Users" && hasMoreUsers) {
            const nextPage = pageUsers + 1;
            fetchData(searchText, "Users", nextPage);
            setPageUsers(nextPage);
          } else if (selectedTab === "Wheels" && hasMoreWheels) {
            const nextPage = pageWheels + 1;
            fetchData(searchText, "Wheels", nextPage);
            setPageWheels(nextPage);
          }
        }
      }
    };

    const container = itemsContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [
    loading,
    hasMoreUsers,
    hasMoreWheels,
    selectedTab,
    searchText,
    pageUsers,
    pageWheels,
  ]);

  useEffect(() => {
    // kelly-0923-P1-search-default-tab-relevance-and-wheel-count-mismatch
    // (finding 1): the Users-empty-Wheels-has-matches fallback in fetchData
    // already fetched Wheels for THIS query before calling setSelectedTab
    // here -- without this guard, this effect would immediately re-fetch the
    // exact same Wheels page 1 a second time.
    if (skipNextTabEffectRef.current) {
      skipNextTabEffectRef.current = false;
      return;
    }
    setHighlightIndex(-1); // EW-1182: switching tabs drops any stale highlight.
    if (searchText.trim()) {
      setLoading(true);
      if (selectedTab === "Users") {
        setPageUsers(1);
        setHasMoreUsers(true);
        setUsers([]);
        fetchData(searchText, "Users", 1);
      } else if (selectedTab === "Wheels") {
        setPageWheels(1);
        setHasMoreWheels(true);
        setWheels([]);
        fetchData(searchText, "Wheels", 1);
      }
    } else {
      // If search text is cleared, reset results
      setUsers([]);
      setWheels([]);
      setShowResults(false);
      setLoading(false); // no query => no request => nothing else clears it
    }
  }, [selectedTab]);

  const onCancel = () => {
    // Cancel the in-flight debounce FIRST. Without this the last keystroke's
    // pending timer still fired ~400ms after the X was clicked and searched for
    // a query the user had already cleared — re-opening the dropdown with rows
    // that match nothing in the (now empty) box. Measured on web-stg 2026-07-24.
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    // GS-H11: clearing the debounce stops a request that has not STARTED yet,
    // but one already in flight still resolves. Supersede it too, or it lands
    // after the X and repopulates a dropdown the user just dismissed.
    if (SEARCH_INPUT_HARDENING_ENABLED) {
      requestSeqRef.current.invalidate();
    }
    setTypingTimeout(null);
    setLoading(false);
    setSearchText("");
    setShowResults(false);
    setUsers([]);
    setWheels([]);
    setHighlightIndex(-1);
    setDismissed(false);
    if (SEARCH_INPUT_HARDENING_ENABLED) {
      setFetchError(false); // EW-1181: clearing the box clears the error banner.
      setFetchErrorStatus(null);
    }
    // GS-2.14 (qa-validator round 3, F-R3-B hardening): the clear (X) can be
    // clicked mid-composition. `compositionend` is spec-guaranteed to fire on
    // blur, and clicking the X does blur the field -- but this is a defensive
    // backstop, not a fix for a measured live defect (F-R3-B could not be
    // reproduced; jsdom cannot simulate real IME). Touches neither `loading`
    // nor any timer, so unlike rounds 1/2's fixes it cannot reintroduce R2-F3.
    composingRef.current = false;
    setComposing(false);
  };

  // EW-1182: wire the results dropdown for keyboard users. searchKeyboardNav
  // decides the target index; opening a result dispatches a native click on the
  // SAME row the mouse clicks (UserItem/WheelItem own the navigation), so the
  // mouse open path is reused verbatim rather than duplicated. Flag-gated: when
  // SEARCH_KEYBOARD_NAV_ENABLED is OFF this handler is never bound.
  const handleSearchKeyDown = (event) => {
    if (!SEARCH_KEYBOARD_NAV_ENABLED) return;
    const list = selectedTab === "Users" ? users : wheels;
    const action = searchNavAction(event.key, highlightIndex, list.length);
    if (!action) return;

    if (action.type === "close") {
      // Escape: actually CLOSE the popup. setShowResults(false) alone only flips
      // aria-expanded — the render gate keeps it mounted while the query text is
      // still in the input — so an explicit `dismissed` override is what unmounts
      // it. Focus stays on the textbox (ARIA APG combobox behavior). Reopen by
      // typing again.
      event.preventDefault();
      setDismissed(true);
      setShowResults(false);
      setHighlightIndex(-1);
      return;
    }

    event.preventDefault();
    setDismissed(false); // Arrow/Enter after an Escape re-opens the dropdown.
    if (action.type === "highlight") {
      setHighlightIndex(action.index);
    } else if (action.type === "open") {
      const nodes = itemsContainerRef.current?.querySelectorAll(
        "[data-search-result-item]"
      );
      const node = nodes && nodes[action.index];
      if (node) node.click(); // same synthetic onClick the mouse triggers
    }
  };

  // EW-1182: keep the keyboard-highlighted row scrolled into view. Instant
  // (block:"nearest") — no smooth-scroll motion. Flag-gated.
  useEffect(() => {
    if (!SEARCH_KEYBOARD_NAV_ENABLED || highlightIndex < 0) return;
    const nodes = itemsContainerRef.current?.querySelectorAll(
      "[data-search-result-item]"
    );
    const node = nodes && nodes[highlightIndex];
    if (node) node.scrollIntoView({ block: "nearest" });
  }, [highlightIndex]);

  const iconVariants = searchIconVariants(reduceMotion);

  const clearIconVariants = searchClearIconVariants(reduceMotion);

  // search-comp-vY-01: computed once and reused for both the render gate and
  // the mobile-overlay flag below, so they can never disagree about whether
  // the results panel is actually open.
  const resultsOpen = searchResultsOpen({
    dismissed,
    showResults,
    searchText,
    loading,
  });

  // vy-0914-resp-all-header-search-input-collapses-at-390-fix-0925: MEASURED
  // live on web-dev (main.7adbc99c.js, 390x844, /discover) -- GalleryHeader's
  // right-hand icon cluster is `noShrink` (WheelGallery/Header/styles.js
  // FlexContainer), so this component's own left-hand FlexContainer
  // (minWidth:0) absorbs 100% of the header squeeze at 390px, landing
  // SearchBar's Container at ~48px and its TextInput pinned to its own 33px
  // min-width floor -- a bare icon square with the "Search iYuga" placeholder
  // entirely clipped (padding-left:30px leaves ~3px of content box). That
  // squeeze is an accepted, documented trade-off (see FlexContainer's
  // header-390-overflow-reopened note), so the fix is not to fight it but to
  // let FOCUS ALONE (not just an in-flight query/results) trigger the
  // existing full-viewport mobile overlay below -- previously `resultsOpen`
  // required non-empty searchText/showResults/loading, so tapping the empty
  // collapsed icon did nothing (MEASURED: box stayed 33px wide after a real
  // .click()). Now the first tap expands the field to the full-width
  // overlay, where the placeholder is legible and there's room to type,
  // before any query has been sent.
  const mobileOverlayActive = isMobilePortrait && (isFocused || resultsOpen);

  return (
    <Styled.Container
      ref={searchContainerRef}
      $mobileOverlayActive={mobileOverlayActive}
      $overlayFieldOnly={mobileOverlayActive && !resultsOpen}
    >
      {/* Politely announce what the search actually returned. MEASURED web-stg
          2026-07-28: the live dropdown had ZERO live regions, so a screen-reader
          user got silence — no result count, and no word that "No Users Found"
          had rendered. Mounted unconditionally (not inside the dropdown) because
          a live region must already exist in the DOM before its text changes;
          a region that appears together with its own text is unreliably
          announced. polite + aria-atomic so each new count replaces the last
          rather than interrupting the user mid-keystroke. */}
      {SEARCH_KEYBOARD_NAV_ENABLED && (
        <Styled.VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
          {searchAnnouncement({
            // GS-2.14/GS-8.8 (qa-validator round 2 F2): a composing keystroke
            // is busy from the user's POV even though no real fetch is in
            // flight yet -- announce "Searching…", not a false result count.
            loading: loading || composing,
            fetchError,
            searchText,
            selectedTab,
            count: selectedTab === "Users" ? users.length : wheels.length,
          })}
        </Styled.VisuallyHidden>
      )}
      <Styled.SearchContainer>
        <AnimatePresence initial={false}>
          {isFocused ? (
            <Styled.SearchIcon
              key="active"
              $unifyFieldBorder={SEARCH_UNIFIED_FIELD_BORDER_ENABLED}
              src={
                SEARCH_UNIFIED_FIELD_BORDER_ENABLED
                  ? ASSETS.SearchIcon2
                  : ASSETS.SearchIconActive
              }
              alt="Active Search Icon"
              variants={iconVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.6, ease: "easeInOut" }}
            />
          ) : (
            <Styled.SearchIcon
              key="inactive"
              $unifyFieldBorder={SEARCH_UNIFIED_FIELD_BORDER_ENABLED}
              src={ASSETS.SearchIcon2}
              alt="Inactive Search Icon"
              variants={iconVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.6, ease: "easeInOut" }}
            />
          )}
        </AnimatePresence>
        <Styled.TextInput
          $unifyFieldBorder={SEARCH_UNIFIED_FIELD_BORDER_ENABLED}
          onChange={handleInputChange}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          onKeyDown={SEARCH_KEYBOARD_NAV_ENABLED ? handleSearchKeyDown : undefined}
          placeholder="Search iYuga"
          value={searchText}
          onFocus={() => {
            setIsFocused(true);
            // Returning to the field re-opens a click-away-dismissed dropdown,
            // so the user gets their results back by clicking the box rather
            // than having to retype. This does NOT weaken Escape: Escape keeps
            // focus ON the input (ARIA APG combobox behavior), so no focus event
            // fires and the panel stays closed until the user types or arrows.
            if (SEARCH_KEYBOARD_NAV_ENABLED) {
              setDismissed(false);
            }
          }}
          onBlur={() => {
            setIsFocused(false);
            // GS-2.14 (qa-validator round 3, F-R3-B hardening): a spec-
            // compliant browser fires compositionend before blur, so this is
            // belt-and-suspenders, not a fix for a reproduced live defect.
            // Same no-loading/no-timer shape as onCancel's backstop above.
            composingRef.current = false;
            setComposing(false);
          }}
          {...(SEARCH_INPUT_HARDENING_ENABLED
            ? { maxLength: SEARCH_MAX_QUERY_LEN }
            : {})}
          {...(SEARCH_KEYBOARD_NAV_ENABLED
            ? {
                role: "combobox",
                "aria-label": "Search iYuga",
                "aria-autocomplete": "list",
                "aria-expanded": resultsOpen,
                "aria-controls": SEARCH_LISTBOX_ID,
                "aria-activedescendant":
                  highlightIndex >= 0
                    ? searchOptionId(selectedTab, highlightIndex)
                    : undefined,
              }
            : {})}
        />
        {searchText && (
          <Styled.ClearIcon
            key="clear"
            src={ASSETS.ClearIcon}
            onClick={onCancel}
            // The clear (×) advertises itself as a button (role="button",
            // tabIndex={0}) but shipped with onClick ONLY, so it took keyboard
            // focus and then did nothing — a focusable-but-inert control (G-20).
            // MEASURED web-stg 2026-07-28: focus lands on the icon, Enter leaves
            // the query "a" unchanged, Space leaves it "a"; a mouse click on the
            // same node clears it to "" (positive control). Enter AND Space both
            // activate, matching the native <button> contract role="button"
            // promises; preventDefault stops Space from page-scrolling.
            onKeyDown={
              SEARCH_KEYBOARD_NAV_ENABLED
                ? (event) => {
                    if (!isActivationKey(event.key)) return;
                    event.preventDefault();
                    onCancel();
                  }
                : undefined
            }
            variants={clearIconVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.3 }}
            whileHover="hover"
            whileTap="tap"
            alt="Clear Search"
            aria-label="Clear search input"
            role="button"
            tabIndex={0}
          />
        )}
      </Styled.SearchContainer>
      {resultsOpen && (
        <Styled.ResultsContainer>
          <>
            <Styled.TabsContainer>
              <Tabs
                categories={BASE_TABS}
                selectedCategory={selectedTab}
                onCategoryChange={(_, val) => setSelectedTab(val)}
                tabStyle={{ maxWidth: "20%" }}
              />
            </Styled.TabsContainer>

            <Styled.ItemsContainer
              ref={itemsContainerRef}
              {...(SEARCH_KEYBOARD_NAV_ENABLED
                ? { id: SEARCH_LISTBOX_ID, role: "listbox" }
                : {})}
            >
              {selectedTab === "Users" && users.length > 0 && (
                <Styled.ResultType>
                  {users.map((item, index) => (
                    <UserItem
                      key={item.id}
                      item={item}
                      setSearchText={setSearchText}
                      setModalOpen={setModalOpen}
                      setOnLeave={setOnLeave}
                      wheelInformation={wheelInformation}
                      prevIndexRef={prevIndexRef}
                      setShowResults={setShowResults}
                      handleNavigate={handleNavigate}
                      kbdNav={SEARCH_KEYBOARD_NAV_ENABLED}
                      isHighlighted={
                        SEARCH_KEYBOARD_NAV_ENABLED && highlightIndex === index
                      }
                      optionId={searchOptionId("Users", index)}
                    />
                  ))}
                </Styled.ResultType>
              )}

              {selectedTab === "Wheels" && wheels.length > 0 && (
                <Styled.ResultType>
                  {wheels.map((item, index) => (
                    <WheelItem
                      key={item.id}
                      item={item}
                      setModalOpen={setModalOpen}
                      setOnLeave={setOnLeave}
                      wheelInformation={wheelInformation}
                      prevIndexRef={prevIndexRef}
                      setShowResults={setShowResults}
                      handleNavigate={handleNavigate}
                      kbdNav={SEARCH_KEYBOARD_NAV_ENABLED}
                      isHighlighted={
                        SEARCH_KEYBOARD_NAV_ENABLED && highlightIndex === index
                      }
                      optionId={searchOptionId("Wheels", index)}
                    />
                  ))}
                </Styled.ResultType>
              )}

              {/* EW-1181: a failed search shows an explicit error instead of
                  silently keeping stale rows / a misleading "No … Found". */}
              {shouldShowSearchError({
                hardeningOn: SEARCH_INPUT_HARDENING_ENABLED,
                fetchError,
                loading,
              }) && (
                <Styled.Loader>
                  <TextView
                    role="alert"
                    style={{
                      fontWeight: 500,
                      marginLeft: 10,
                      marginBottom: 5,
                    }}
                  >
                    {getSearchErrorMessage(fetchErrorStatus)}
                  </TextView>
                </Styled.Loader>
              )}

              {shouldShowEmptyState({
                hardeningOn: SEARCH_INPUT_HARDENING_ENABLED,
                fetchError,
                // GS-2.14/GS-8.8 (qa-validator round 2 F3/F4): a composing
                // keystroke must never satisfy the empty-state gate -- rounds
                // 1+2 both found a way for it to (an early-return that skipped
                // `loading`, then a timer that cleared `loading` mid-flight).
                // `composing` is driven by the real compositionstart/end
                // lifecycle, not a guess, so this can't race a real fetch.
                loading: loading || composing,
                searchText,
                listEmpty:
                  (selectedTab === "Users" && users.length === 0) ||
                  (selectedTab === "Wheels" && wheels.length === 0),
              }) && (
                <Styled.EmptyResults>
                  <img
                    src={NotFound}
                    alt="No search results"
                    width={80}
                    height={80}
                  />
                  <TextView
                    style={{
                      fontWeight: 500,
                      marginTop: 12,
                    }}
                  >
                    {selectedTab === "Users"
                      ? "No Users Found"
                      : "No Wheels Found"}
                  </TextView>
                  <TextView
                    style={{
                      fontWeight: 400,
                      fontSize: 13,
                      color: colors.textGrey,
                      marginTop: 4,
                    }}
                  >
                    Try a different search term.
                  </TextView>
                </Styled.EmptyResults>
              )}

              {/* Loader for infinite scroll -- also shown while composing
                  (GS-2.14/GS-8.8), so an IME user sees busy, not a false
                  empty state, without `loading` itself ever being faked. */}
              {(loading || composing) && (
                <Styled.InfiniteLoader>
                  <BrandWheelLoader size={40} label="Loading" />
                </Styled.InfiniteLoader>
              )}
            </Styled.ItemsContainer>
          </>
        </Styled.ResultsContainer>
      )}
    </Styled.Container>
  );
}
